import { useState, useEffect } from 'react';
import { Save, Shield, Download, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

function Toggle({ on, onToggle }) {
  return (
    <button onClick={onToggle} className={`w-11 h-6 rounded-full transition-colors relative ${on ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}>
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${on ? 'left-5' : 'left-0.5'}`}/>
    </button>
  );
}

export default function PrivacySettingsPage() {
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();

  const [settings, setSettings] = useState({
    data_retention_months: 60,
    auto_delete_resigned: false,
    mask_aadhaar: true,
    mask_bank: true,
    audit_log_enabled: true,
    allow_employee_export: false,
  });
  const [exportDone, setExportDone] = useState(false);
  const s = (k, v) => setSettings(f => ({ ...f, [k]: v }));

  useEffect(() => {
    const p = data?.data?.settings?.privacy;
    if (p) setSettings(prev => ({ ...prev, ...p }));
  }, [data]);

  const inputCls = "h-10 px-3 w-24 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  const toggleRows = [
    ['auto_delete_resigned', 'Auto-anonymize resigned employees', 'Replace PII with anonymized data after retention period'],
    ['mask_aadhaar', 'Mask Aadhaar numbers', 'Show only last 4 digits (XXXX XXXX 1234) in all views'],
    ['mask_bank', 'Mask bank account numbers', 'Show only last 4 digits in payslips and reports'],
    ['audit_log_enabled', 'Enable audit log', 'Track all data access and changes (recommended)'],
    ['allow_employee_export', 'Allow HR to export employee data', 'Controls CSV/Excel export in employee list'],
  ];

  const handleSave = () => {
    mutate({ settings: { privacy: settings } }, {
      onSuccess: () => toast.success('Privacy settings saved'),
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-xl">
      <PageHeader title="Privacy & Data" breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Privacy & Data' }]}/>

      <div className="card space-y-5 p-5">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-[var(--accent)]"/>
          <h3 className="text-[14px] font-bold text-[var(--text-primary)]">Data Retention</h3>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[13px] font-semibold text-[var(--text-primary)]">Retain data for</p>
            <p className="text-[12px] text-[var(--text-secondary)]">Attendance, payroll, and leave records older than this are archived</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" min="6" max="120" value={settings.data_retention_months}
              onChange={e => s('data_retention_months', +e.target.value)} className={inputCls}/>
            <span className="text-[13px] text-[var(--text-secondary)]">months</span>
          </div>
        </div>
      </div>

      <div className="card space-y-1 divide-y divide-[var(--border)] px-5">
        {toggleRows.map(([key, label, desc]) => (
          <div key={key} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
            <div className="pr-4">
              <p className="text-[13px] font-semibold text-[var(--text-primary)]">{label}</p>
              <p className="text-[12px] text-[var(--text-secondary)]">{desc}</p>
            </div>
            <Toggle on={settings[key]} onToggle={() => s(key, !settings[key])}/>
          </div>
        ))}
      </div>

      <div className="card space-y-3 p-5">
        <h3 className="text-[14px] font-bold text-[var(--text-primary)]">Data Export & Deletion</h3>
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-[8px] bg-[var(--bg-muted)] text-[12px] text-[var(--text-secondary)]">
          <Info size={12} className="mt-0.5 shrink-0"/>
          <span>Exports include all company data in JSON format. Deletion is permanent and cannot be undone.</span>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" icon={Download} onClick={() => { setExportDone(true); setTimeout(() => setExportDone(false), 3000); }}>
            {exportDone ? 'Export Queued ✓' : 'Export All Data'}
          </Button>
          <button className="px-4 py-2 rounded-[10px] border border-[var(--danger)]/40 text-[var(--danger)] text-[13px] font-semibold hover:bg-[var(--danger)]/10 transition-colors flex items-center gap-2">
            <Trash2 size={14}/>
            Request Account Deletion
          </button>
        </div>
      </div>

      <Button icon={Save} onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Settings'}</Button>
    </div>
  );
}
